import { getRangePickerValue } from '@intelligence/v3/filterControls/rangePicker/utils';
import { getStringArrayValue } from '@intelligence/v3/filterControls/shared/valueUtils';
import { DOWNLOAD_CFG_KEY } from '@intelligence/v3/constants';
import { buildRobloxGenreFilterParam } from '@intelligence/v3/utils/buildRobloxGenreFilter';
import { buildRobloxReleaseDateFilterParam } from '@intelligence/v3/utils/robloxReleaseDateFilter';
import { getRobloxFetchDateRange } from '@intelligence/v3/utils/robloxFilterDate';
import {
  getDefaultFilterValues,
  ROBLOX_TOP_CHARTS_DOWNLOAD_PAGE_SIZE,
  ROBLOX_TOP_CHARTS_DOWNLOAD_USE_SSE_PAGE,
  ROBLOX_TOP_CHARTS_FIXED_FETCH_PARAMS,
} from '../constants';
import {
  buildRobloxTopChartsOrderBy,
  ROBLOX_TOP_CHARTS_DEFAULT_SORT_KEY,
} from '../config/robloxTopChartsSort';
import {
  getRobloxTopChartsDownloadCustomFields,
  getRobloxTopChartsDownloadMetrics,
} from '../config/robloxTopChartsDownloadConfig';

import type { IRobloxTopChartsDownloadFormValues } from '../components/RobloxTopChartsDownload/types';
import type { IRobloxTopChartsFilterValues } from '../types';
import type { IMainGenre } from '@intelligence/types/common';

interface IBuildRobloxTopChartsDownloadParamsOptions {
  filterValues: IRobloxTopChartsFilterValues;
  formValues: IRobloxTopChartsDownloadFormValues;
  regionOptions?: IRecord[];
  sortKey?: string;
  sortOrder?: 'asc' | 'desc';
  robloxGenreOptions?: IMainGenre[];
}

const BASE_GROUP_BY = ['game_id'];

const buildDownloadGroupBy = (
  formValues: IRobloxTopChartsDownloadFormValues,
): string[] => {
  const groupBy = [...BASE_GROUP_BY];

  if (formValues.date_method !== 'aggregated') {
    groupBy.push('date');
  }

  if (formValues.region_method === 'market') {
    groupBy.push('region', 'market');
  } else if (formValues.region_method === 'region') {
    groupBy.push('region');
  }

  return groupBy;
};

const buildDownloadTopParam = (
  dataMethod: IRobloxTopChartsDownloadFormValues['data_method'],
  selectTop: number,
  sortMetric: string,
) => {
  if (dataMethod !== 'selectTop' || selectTop <= 0) {
    return undefined;
  }

  return {
    group_by: [...BASE_GROUP_BY],
    order_by: [`${sortMetric} desc`],
    size: selectTop,
  };
};

const buildUseMarketInfo = (
  regionMethod: IRobloxTopChartsDownloadFormValues['region_method'],
): string[] => {
  if (regionMethod === 'market') {
    return ['market -> region', 'market -> en', 'market -> cn', 'intel_region -> en', 'intel_region -> cn'];
  }

  if (regionMethod === 'region') {
    return ['intel_region -> en', 'intel_region -> cn'];
  }

  return [];
};

export const buildRobloxTopChartsDownloadParams = (
  options: IBuildRobloxTopChartsDownloadParamsOptions,
) => {
  const { filterValues, formValues, regionOptions = [], sortKey, sortOrder, robloxGenreOptions } = options;
  const defaults = getDefaultFilterValues();
  const { granularity } = getRangePickerValue(filterValues.date ?? defaults.date);
  const { start_date: startDate, end_date: endDate } = getRobloxFetchDateRange(filterValues);
  const metrics = getRobloxTopChartsDownloadMetrics();
  const groupBy = buildDownloadGroupBy(formValues);
  const orderByField = buildRobloxTopChartsOrderBy(
    sortKey || ROBLOX_TOP_CHARTS_DEFAULT_SORT_KEY,
    sortOrder || 'desc',
  );
  const genreFilter = buildRobloxGenreFilterParam(
    filterValues.genre ?? defaults.genre,
    robloxGenreOptions,
  );
  const releaseDateFilter = buildRobloxReleaseDateFilterParam(
    filterValues.releaseTime ?? defaults.releaseTime,
  );
  const topParam = buildDownloadTopParam(
    formValues.data_method,
    formValues.data_amount,
    orderByField.split(' ')[0],
  );

  const params: IRecord = {
    start_date: startDate,
    end_date: endDate,
    granularity,
    metrics,
    group_by: groupBy,
    use_entity_type: ROBLOX_TOP_CHARTS_FIXED_FETCH_PARAMS.use_entity_type,
    use_custom_fields: getRobloxTopChartsDownloadCustomFields(
      formValues.gameInfo,
      [...ROBLOX_TOP_CHARTS_FIXED_FETCH_PARAMS.use_custom_fields],
    ),
    markets: getStringArrayValue(filterValues.region ?? defaults.region),
    page: 1,
    page_size: ROBLOX_TOP_CHARTS_DOWNLOAD_PAGE_SIZE,
    order_by: [orderByField],
    filter: {
      ...genreFilter,
      ...releaseDateFilter,
    },
    [DOWNLOAD_CFG_KEY.DATE_METHOD]: formValues.date_method,
    [DOWNLOAD_CFG_KEY.REGION_METHOD]: formValues.region_method,
    [DOWNLOAD_CFG_KEY.DATA_METHOD]: formValues.data_method,
    [DOWNLOAD_CFG_KEY.SELECT_TOP]: formValues.data_amount,
    data_method: formValues.data_method,
    data_amount: formValues.data_amount,
    api_top_applied: Boolean(topParam),
    file_format: formValues.file_format === 'xlsx' ? 0 : 1,
    ratio_show: formValues.ratio_show ? 1 : 0,
    pcu_show: formValues.gameInfo.pcu_show ? 1 : 0,
    favorite_show: formValues.gameInfo.favorite_show ? 1 : 0,
    visit_show: formValues.gameInfo.visit_show ? 1 : 0,
    like_show: formValues.gameInfo.like_show ? 1 : 0,
    unlike_show: formValues.gameInfo.unlike_show ? 1 : 0,
    server_size_show: formValues.gameInfo.server_size_show ? 1 : 0,
    supported_features_show: formValues.gameInfo.supported_features_show ? 1 : 0,
    roblox_genre_show: formValues.gameInfo.roblox_genre_show ? 1 : 0,
    regionOptions,
    filterValues,
    new_style_donwload_file_name: 'Roblox Top Charts',
  };

  const useMarketInfo = buildUseMarketInfo(formValues.region_method);
  if (useMarketInfo.length) {
    params.use_market_info = useMarketInfo;
  }

  if (formValues.data_method === 'checkedLines' && formValues.game_ids.length) {
    params.game_ids = formValues.game_ids;
  }

  return params;
};

export const buildRobloxTopChartsDownloadApiParams = (
  params: IRecord = {},
): IRecord => {
  const dataMethod =
    params?.[DOWNLOAD_CFG_KEY.DATA_METHOD] ?? params?.data_method;
  const selectTop =
    Number(params?.[DOWNLOAD_CFG_KEY.SELECT_TOP] ?? params?.data_amount) || 0;
  const orderBy = params?.order_by || [
    buildRobloxTopChartsOrderBy(ROBLOX_TOP_CHARTS_DEFAULT_SORT_KEY, 'desc'),
  ];

  const apiParams: IRecord = {
    start_date: params.start_date,
    end_date: params.end_date,
    granularity: params.granularity,
    metrics: params.metrics,
    group_by: params.group_by,
    use_entity_type: params.use_entity_type,
    use_custom_fields: params.use_custom_fields,
    markets: params.markets,
    page: params.page || 1,
    page_size: ROBLOX_TOP_CHARTS_DOWNLOAD_PAGE_SIZE,
    order_by: orderBy,
    filter: params.filter || {},
    use_sse_page: { ...ROBLOX_TOP_CHARTS_DOWNLOAD_USE_SSE_PAGE },
  };

  if (Array.isArray(params.use_market_info) && params.use_market_info.length) {
    apiParams.use_market_info = params.use_market_info;
  }

  if (Array.isArray(params.game_ids) && params.game_ids.length) {
    apiParams.game_ids = params.game_ids;
  }

  const topParam = buildDownloadTopParam(
    dataMethod,
    selectTop,
    String(orderBy[0] || '').split(' ')[0]
      || buildRobloxTopChartsOrderBy(ROBLOX_TOP_CHARTS_DEFAULT_SORT_KEY, 'desc').split(' ')[0],
  );

  if (topParam) {
    apiParams.top = topParam;
  }

  return apiParams;
};
